/**
 * Wording of the "กำลังจัดส่ง" cards (components/side/DeliveryCard.vue): phase → Thai title / chip / step, the
 * detail line under the title, "อัปเดต 3 นาทีที่แล้ว", and the detailed card of the owner-approved mockup v4
 * (28 ก.ย.: arrival + round pill, trip line, step times, driver line, DHL latest scan + fold, the message for the
 * customer). The API decides the rules (late / not moving / no new scan); these only word them.
 * Pure functions (test/delivery.spec.ts).
 */
import type { DeliveryCarrier, DeliveryDhl, DeliveryLalamove, DeliveryOrder, DeliveryPhase } from '../api/deliveries'
import { baht, clockTime, shortDate } from './inbox'
import { posDate, relTime } from './webchat'

export type Tone = 'neutral' | 'info' | 'go' | 'done' | 'warn' | 'crit'

export const LALA_STEPS = ['เตรียมอาหาร', 'คนขับมารับ', 'กำลังไปส่ง', 'ถึงลูกค้า'] as const
export const DHL_STEPS = ['เตรียมพัสดุ', 'DHL รับแล้ว', 'กำลังนำส่ง', 'ส่งถึงแล้ว'] as const

interface PhaseText {
  chip: string
  title: string
  tone: Tone
  /** Index in the carrier's steps · null = no step line (upcoming order, or a problem shown as a note). */
  step: number | null
}

const COMMON: Record<'scheduled' | 'delivered', PhaseText> = {
  scheduled: { chip: 'รอส่ง', title: 'รอส่งตามรอบ', tone: 'neutral', step: null },
  delivered: { chip: 'ส่งถึงแล้ว', title: 'ส่งถึงลูกค้าแล้ว', tone: 'done', step: 3 },
}

const LALA: Partial<Record<DeliveryPhase, PhaseText>> = {
  preparing: { chip: 'เตรียมอาหาร', title: 'กำลังเตรียมอาหาร ยังไม่เรียกรถ', tone: 'neutral', step: 0 },
  finding_driver: { chip: 'หาคนขับ', title: 'เรียกรถแล้ว กำลังหาคนขับ', tone: 'info', step: 1 },
  to_pickup: { chip: 'ไปรับอาหาร', title: 'คนขับกำลังไปรับอาหารที่ร้าน', tone: 'info', step: 1 },
  on_the_way: { chip: 'กำลังไปส่ง', title: 'รับอาหารแล้ว กำลังไปส่ง', tone: 'go', step: 2 },
  carrier_cancelled: { chip: 'งานถูกยกเลิก', title: 'งาน Lalamove นี้ถูกยกเลิก', tone: 'warn', step: null },
}

const DHL: Partial<Record<DeliveryPhase, PhaseText>> = {
  preparing: { chip: 'เตรียมพัสดุ', title: 'กำลังเตรียมพัสดุ ยังไม่ออกฉลาก', tone: 'neutral', step: 0 },
  label_created: { chip: 'รอ DHL รับ', title: 'ออกฉลากแล้ว รอ DHL เข้ารับ', tone: 'neutral', step: 0 },
  picked_up: { chip: 'DHL รับแล้ว', title: 'DHL รับพัสดุแล้ว', tone: 'info', step: 1 },
  in_transit: { chip: 'ระหว่างขนส่ง', title: 'พัสดุอยู่ระหว่างขนส่ง', tone: 'info', step: 1 },
  out_for_delivery: { chip: 'กำลังนำส่ง', title: 'DHL กำลังนำส่งถึงลูกค้า', tone: 'go', step: 2 },
  failed: { chip: 'ส่งไม่สำเร็จ', title: 'DHL นำส่งไม่สำเร็จ', tone: 'crit', step: null },
  returned: { chip: 'ตีกลับ', title: 'พัสดุถูกตีกลับ', tone: 'crit', step: null },
  carrier_cancelled: { chip: 'ยกเลิกฉลาก', title: 'ยกเลิกฉลาก DHL แล้ว', tone: 'warn', step: null },
}

const OTHER: Partial<Record<DeliveryPhase, PhaseText>> = {
  preparing: { chip: 'เตรียมอาหาร', title: 'กำลังเตรียมอาหาร', tone: 'neutral', step: null },
  on_the_way: { chip: 'กำลังจัดส่ง', title: 'กำลังจัดส่ง', tone: 'go', step: null },
}

export function phaseText(carrier: DeliveryCarrier, phase: DeliveryPhase): PhaseText {
  if (phase === 'scheduled' || phase === 'delivered') {
    return carrier === 'other' ? { ...COMMON[phase], step: null } : COMMON[phase]
  }
  const table = carrier === 'lalamove' ? LALA : carrier === 'dhl' ? DHL : OTHER
  return table[phase] ?? { chip: 'กำลังจัดส่ง', title: 'กำลังจัดส่ง', tone: 'info', step: null }
}

export function stepsOf(carrier: DeliveryCarrier): readonly string[] {
  return carrier === 'dhl' ? DHL_STEPS : LALA_STEPS
}

export const carrierName = (c: DeliveryCarrier): string => (c === 'lalamove' ? 'Lalamove' : c === 'dhl' ? 'DHL' : 'ขนส่งอื่น')

/**
 * "ส่งวันนี้ · 11.00-13.00 น." · "ส่งพรุ่งนี้" · "ส่งเมื่อวาน" · "ส่ง อ. 29 ก.ย. · 11.00-13.00 น." · DHL: the bill's
 * delivery date is the day DHL picks the parcel up (the customer gets it 1–3 days later, by postcode) → "ส่งออก …".
 */
export function whenText(
  o: Pick<DeliveryOrder, 'delivery_date' | 'slot'> & { carrier?: DeliveryCarrier },
  nowMs: number,
): string | null {
  const day = dayWord(o.delivery_date, nowMs)
  if (!day) return null
  const lead = `${o.carrier === 'dhl' ? 'ส่งออก' : 'ส่ง'}${day}`
  return o.slot ? `${lead} · ${o.slot}` : lead
}

/** "วันนี้" · "พรุ่งนี้" · "เมื่อวาน" · " อ. 29 ก.ย." (a date keeps its leading space) for a Bangkok date. */
function dayWord(date: string | null, nowMs: number): string | null {
  if (!date) return null
  const key = (ms: number) => new Date(ms + 7 * 3600_000).toISOString().slice(0, 10)
  if (date === key(nowMs)) return 'วันนี้'
  if (date === key(nowMs + 86400_000)) return 'พรุ่งนี้'
  if (date === key(nowMs - 86400_000)) return 'เมื่อวาน'
  const d = posDate(date, nowMs)
  return d ? ` ${d}` : null
}

export interface DetailLine {
  text: string
  /** The one thing to tell the customer right now (arrival time). */
  strong?: boolean
}

/** The lines under the title: what the customer most likely wants to know for this phase. */
export function detailLines(o: DeliveryOrder, nowMs: number): DetailLine[] {
  const l = o.lalamove
  const d = o.dhl
  const one = (t: string | null): DetailLine[] => (t ? [{ text: t }] : [])
  switch (o.phase) {
    case 'scheduled':
    case 'preparing':
      return one(whenText(o, nowMs))
    case 'finding_driver':
      return one('รอ Lalamove จับคู่คนขับ')
    case 'to_pickup':
      return one('ยังไม่ถึงร้าน · เวลาถึงโดยประมาณจะขึ้นหลังคนขับรับอาหาร')
    case 'on_the_way': {
      const out: DetailLine[] = []
      if (l?.eta) {
        out.push({
          text: l.eta.arrival ? `คาดว่าถึงประมาณ ${l.eta.arrival} น. · อีก ~${l.eta.minutes} นาที` : `อีก ~${l.eta.minutes} นาที`,
          strong: true,
        })
      }
      if (l && l.stops_ahead !== null) {
        out.push({ text: l.stops_ahead === 0 ? 'ลูกค้าเป็นคิวถัดไป' : `ก่อนถึงคิวลูกค้าอีก ${l.stops_ahead} จุด` })
      }
      if (!out.length && l?.picked_up_at) out.push({ text: `ออกจากร้าน ${clockTime(l.picked_up_at)} น.` })
      return out.length ? out : one(whenText(o, nowMs))
    }
    case 'delivered':
      return one(o.delivered_at ? `ส่งถึงเมื่อ ${dayClock(o.delivered_at, nowMs)}` : null)
    case 'carrier_cancelled':
      return one(
        o.carrier === 'lalamove'
          ? 'ถ้าทีมเรียกรถใหม่ในแอป Lalamove ระบบจะยังไม่เห็น ตรวจที่หน้า Live Tracking'
          : 'ตรวจที่หน้าใบสั่งงาน DHL ว่าออกฉลากใหม่หรือยัง',
      )
    default:
      if (d) {
        const scan = [d.status_text, d.event_at ? dayClock(d.event_at, nowMs) : null, d.place].filter(Boolean)
        return one(scan.length ? `สแกนล่าสุด: ${scan.join(' · ')}` : null)
      }
      return one(whenText(o, nowMs))
  }
}

/** The same lines in one string (screen readers, tests). */
export const detailText = (o: DeliveryOrder, nowMs: number): string | null =>
  detailLines(o, nowMs)
    .map((l) => l.text)
    .join(' · ') || null

/** "14:05 น." today · "เมื่อวาน 18:40 น." · "25 ก.ย. 18:40 น." */
export function dayClock(iso: string, nowMs: number): string {
  const key = (ms: number) => new Date(ms + 7 * 3600_000).toISOString().slice(0, 10)
  const t = Date.parse(iso)
  const time = `${clockTime(iso)} น.`
  if (key(t) === key(nowMs)) return time
  if (key(t) === key(nowMs - 86400_000)) return `เมื่อวาน ${time}`
  return `${posDate(key(t), nowMs) ?? ''} ${time}`.trim()
}

/** "อัปเดต 3 นาทีที่แล้ว" · "อัปเดตเมื่อสักครู่" — when the carrier last said something (not when we asked). */
export function updatedText(iso: string | null, nowMs: number): string | null {
  if (!iso) return null
  const rel = relTime(iso, nowMs)
  return rel === 'เมื่อสักครู่' ? 'อัปเดตเมื่อสักครู่' : `อัปเดต ${rel}`
}

/** Driver position age for staff: fresh = fine; old = the driver app may be asleep (ETA hidden then). */
export function locationText(l: DeliveryOrder['lalamove']): { text: string; stale: boolean } | null {
  if (!l?.location) return null
  const m = l.location.age_min
  if (l.location.fresh) return { text: m < 1 ? 'ตำแหน่งคนขับล่าสุดเมื่อสักครู่' : `ตำแหน่งคนขับล่าสุด ${m} นาทีที่แล้ว`, stale: false }
  return { text: `ตำแหน่งคนขับไม่อัปเดตมา ${m >= 60 ? `${Math.floor(m / 60)} ชม.` : `${m} นาที`}`, stale: true }
}

const PAY: Record<string, string> = {
  BankTransfer: 'โอนแล้ว',
  CashOnDelivery: 'เก็บเงินปลายทาง',
  promptpay: 'ชำระพร้อมเพย์แล้ว',
  CreditCard: 'ชำระบัตรแล้ว',
  shopee: 'Shopee',
}
export const payText = (p: DeliveryOrder['payment']): string | null => (p?.method ? (PAY[p.method] ?? p.method) : null)

/** "รอบ 2/4 · 21 มื้อ" — meals, snacks and the pack round. */
export function itemsText(o: Pick<DeliveryOrder, 'meals' | 'snacks' | 'round'>): string {
  const parts: string[] = []
  if (o.round) parts.push(`รอบ ${o.round.no}/${o.round.of}`)
  if (o.meals) parts.push(`${o.meals} มื้อ`)
  if (o.snacks) parts.push(`ขนม ${o.snacks}`)
  return parts.join(' · ')
}

/** "7127…0646" for the card; the full number is copied. */
export const shortTracking = (no: string): string => (no.length > 12 ? `${no.slice(0, 4)}…${no.slice(-4)}` : no)

// ---------------------------------------------------------------- detailed card (mockup v4, 28 ก.ย.)

/** Same thresholds as the API (it only sends the value past them; the card checks again). */
export const STILL_MIN = 10
export const DHL_QUIET_H = 24

const bkkKey = (ms: number): string => new Date(ms + 7 * 3600_000).toISOString().slice(0, 10)

/**
 * The detailed layout: Lalamove once the driver has the food (and after delivery), DHL once DHL has the parcel.
 * The kitchen phases (a DHL label waiting for pickup included) and the cancelled trips (a staff note) stay compact.
 */
export function isDetailed(o: DeliveryOrder): boolean {
  if (o.carrier === 'lalamove') return !!o.lalamove && (o.phase === 'on_the_way' || o.phase === 'delivered')
  if (o.carrier === 'dhl') return !!o.dhl && !['scheduled', 'preparing', 'label_created', 'carrier_cancelled'].includes(o.phase)
  return false
}

/** "18 นาที" · "1 ชม." · "1 ชม. 20 นาที" */
export function durText(min: number): string {
  const m = Math.max(0, Math.round(min))
  if (m < 60) return `${m} นาที`
  const h = Math.floor(m / 60)
  return m % 60 ? `${h} ชม. ${m % 60} นาที` : `${h} ชม.`
}

/** "31 ชม." · "3 วัน" */
export const hoursText = (h: number): string => (h < 72 ? `${h} ชม.` : `${Math.floor(h / 24)} วัน`)

/** "3.2 กก." · "850 ก." */
export const weightText = (g: number | null | undefined): string | null =>
  !g ? null : g >= 1000 ? `${(g / 1000).toFixed(1).replace(/\.0$/, '')} กก.` : `${g} ก.`

/** The arrival time the ETA service gave ("12:26"), or now + minutes. */
function arrivalOf(l: DeliveryLalamove | null, nowMs: number): string | null {
  if (!l?.eta) return null
  return l.eta.arrival ?? clockTime(new Date(nowMs + l.eta.minutes * 60_000).toISOString())
}

export interface Hero {
  big: string
  sub: string | null
  /** The sub line is a warning (delivered after the round). */
  warn?: boolean
}

/** The big line: arrival time on the way · delivered time · the parcel's status. */
export function heroOf(o: DeliveryOrder, nowMs: number): Hero {
  const l = o.lalamove
  if (o.phase === 'delivered') {
    const at = o.delivered_at ?? l?.delivered_at ?? null
    const big = at ? `ส่งถึง${whenShort(at, nowMs)}` : 'ส่งถึงลูกค้าแล้ว'
    const t = o.timing
    if (o.carrier === 'lalamove' && t?.basis === 'delivered' && o.slot) {
      return t.late_min > 0 ? { big, sub: `เลยรอบ ${o.slot} ไป ${durText(t.late_min)}`, warn: true } : { big, sub: `ทันรอบ ${o.slot}` }
    }
    return { big, sub: null }
  }
  if (o.carrier === 'lalamove' && o.phase === 'on_the_way') {
    const arr = arrivalOf(l, nowMs)
    if (l?.eta && arr) return { big: `ถึงประมาณ ${arr} น.`, sub: `อีก ~${durText(l.eta.minutes)}` }
    return { big: 'รับอาหารแล้ว กำลังไปส่ง', sub: l?.picked_up_at ? `ออกจากร้าน ${clockTime(l.picked_up_at)} น.` : null }
  }
  return { big: phaseText(o.carrier, o.phase).title, sub: null }
}

export interface Pill {
  text: string
  tone: 'ok' | 'warn'
}

/** Lalamove on the way: in time for the round or not (API `timing`) · DHL: no new scan for ≥ 24 h (API `quiet_h`). */
export function pillOf(o: DeliveryOrder): Pill | null {
  const t = o.timing
  if (o.carrier === 'lalamove' && o.phase === 'on_the_way' && t?.basis === 'eta' && o.slot && o.lalamove?.eta) {
    return t.late_min > 0
      ? { tone: 'warn', text: `อาจเลยรอบส่ง ${o.slot} ไป ~${durText(t.late_min)}` }
      : { tone: 'ok', text: `ทันรอบส่ง ${o.slot}` }
  }
  const q = o.dhl?.quiet_h
  if (o.carrier === 'dhl' && q && q >= DHL_QUIET_H && o.phase !== 'delivered') {
    return { tone: 'warn', text: `ไม่มีสแกนใหม่ ${hoursText(q)}` }
  }
  return null
}

/** "ทริปนี้ 4 จุด · ส่งแล้ว 2 · อีก 1 จุดก่อนถึงลูกค้า" — only the numbers the carrier gave. */
export function tripParts(o: DeliveryOrder): { text: string; strong?: boolean }[] {
  const l = o.lalamove
  if (!l || o.phase !== 'on_the_way') return []
  const out: { text: string; strong?: boolean }[] = []
  if (l.stops_total && l.stops_total > 1) {
    out.push({ text: `ทริปนี้ ${l.stops_total} จุด` })
    if (l.stops_done !== null && l.stops_done !== undefined) out.push({ text: `ส่งแล้ว ${l.stops_done}` })
  }
  if (l.stops_ahead === 0) out.push({ text: 'ลูกค้าเป็นคิวถัดไป', strong: true })
  else if (l.stops_ahead !== null && l.stops_ahead > 0) out.push({ text: `อีก ${l.stops_ahead} จุดก่อนถึงลูกค้า`, strong: true })
  return out
}

export interface StepView {
  label: string
  state: 'done' | 'now' | 'todo'
  /** "11:34" today · "26 ก.ย." another day · "~12:26" the ETA · null = unknown (blank, never guessed). */
  time: string | null
  est: boolean
  /** Full time for the tooltip. */
  title: string | null
}

const stepClock = (iso: string, nowMs: number): string =>
  bkkKey(Date.parse(iso)) === bkkKey(nowMs) ? clockTime(iso) : shortDate(iso)

/** The 4 steps with a time under each (API `steps_at`) — delivered = all done. */
export function stepViews(o: DeliveryOrder, nowMs: number): StepView[] | null {
  const p = phaseText(o.carrier, o.phase)
  if (p.step === null) return null
  const at = o.steps_at ?? []
  const eta = o.carrier === 'lalamove' && o.phase === 'on_the_way' ? arrivalOf(o.lalamove, nowMs) : null
  return stepsOf(o.carrier).map((label, i) => {
    const iso = at[i] ?? null
    const est = !iso && i === 3 && !!eta
    const state = o.phase === 'delivered' || i < (p.step ?? 0) ? 'done' : i === p.step ? 'now' : 'todo'
    return {
      label,
      state,
      time: iso ? stepClock(iso, nowMs) : est ? `~${eta}` : null,
      est,
      title: iso ? dayClock(iso, nowMs) : est ? `เวลาถึงโดยประมาณ ${eta} น.` : null,
    }
  })
}

/** Under the driver's name: plate · where (position age / not moving) · how far. */
export function driverLine(l: DeliveryLalamove | null): { parts: string[]; warn: string | null } | null {
  if (!l?.driver) return null
  const parts: string[] = []
  let warn: string | null = null
  if (l.driver.plate) parts.push(l.driver.plate)
  if (l.still_min && l.still_min >= STILL_MIN) warn = `ไม่ขยับมา ${durText(l.still_min)}`
  else if (l.location) {
    const m = l.location.age_min
    if (l.location.fresh) parts.push(m < 1 ? 'ตำแหน่งเมื่อสักครู่' : `ตำแหน่ง ${m} นาทีที่แล้ว`)
    else warn = `ตำแหน่งไม่อัปเดตมา ${m >= 60 ? `${Math.floor(m / 60)} ชม.` : `${m} นาที`}`
  }
  if (l.distance_km !== null && l.distance_km !== undefined) parts.push(`ห่าง ~${l.distance_km.toFixed(1)} กม.`)
  return { parts, warn }
}

export interface ScanView {
  text: string
  place: string | null
  time: string
  title: string
}

const scanTime = (iso: string, nowMs: number): string =>
  bkkKey(Date.parse(iso)) === bkkKey(nowMs) ? clockTime(iso) : `${shortDate(iso)} ${clockTime(iso)}`

/** DHL: the latest scan on the card, the others behind "ดูสแกนก่อนหน้าอีก N รายการ" (owner: "เห็นอันล่าสุดพอ"). */
export function scanFold(d: DeliveryDhl | null, nowMs: number): { latest: ScanView | null; rest: ScanView[] } {
  if (!d) return { latest: null, rest: [] }
  let scans = (d.scans ?? []).filter((x) => x.text || x.at)
  if (!scans.length && (d.status_text || d.event_at)) {
    scans = [{ code: d.status_code, key: null, text: d.status_text, place: d.place, at: d.event_at }]
  }
  const views = scans.map((x) => ({
    text: x.text ?? 'สแกน DHL',
    place: x.place,
    time: x.at ? scanTime(x.at, nowMs) : '',
    title: x.at ? dayClock(x.at, nowMs) : '',
  }))
  return { latest: views[0] ?? null, rest: views.slice(1) }
}

/** The small line under a detailed card: [DHL ship-out day] · round · pack · total · payment · weight. */
export function detailMeta(o: DeliveryOrder, nowMs: number): string {
  const parts: (string | null)[] = []
  if (o.carrier === 'dhl' && o.phase !== 'delivered') parts.push(whenText({ ...o, slot: null }, nowMs))
  if (o.round) parts.push(`รอบ ${o.round.no}/${o.round.of}`)
  parts.push(o.pack || (o.meals ? `${o.meals} มื้อ` : null))
  if (o.snacks) parts.push(`ขนม ${o.snacks}`)
  if (o.total !== null) parts.push(baht(o.total))
  parts.push(payText(o.payment))
  parts.push(weightText(o.dhl?.weight_g))
  return parts.filter(Boolean).join(' · ')
}

/** "เมื่อ 11:48 น." · "เมื่อวาน 11:48 น." · " 26 ก.ย. 11:48 น." — after "ส่งถึง" on the card. */
function whenShort(iso: string, nowMs: number): string {
  const k = bkkKey(Date.parse(iso))
  const t = `${clockTime(iso)} น.`
  if (k === bkkKey(nowMs)) return `เมื่อ ${t}`
  if (k === bkkKey(nowMs - 86400_000)) return `เมื่อวาน ${t}`
  return ` ${shortDate(iso)} ${t}`
}

/** "เมื่อ 11:48 น." · "เมื่อวาน เวลา 11:48 น." · "เมื่อ 26 ก.ย. เวลา 11:48 น." — for the customer. */
function whenSaid(iso: string, nowMs: number): string {
  const k = bkkKey(Date.parse(iso))
  const t = `${clockTime(iso)} น.`
  if (k === bkkKey(nowMs)) return `เมื่อ ${t}`
  if (k === bkkKey(nowMs - 86400_000)) return `เมื่อวาน เวลา ${t}`
  return `เมื่อ ${shortDate(iso)} เวลา ${t}`
}

/**
 * A polite message for this customer built only from the card (ค่ะ/นะคะ · no promise beyond the data · the bill
 * link = the customer's own tracking page). Staff put it in the typing box and edit before sending — never sent here.
 */
export function customerMessage(o: DeliveryOrder, nowMs: number): string | null {
  const link = o.bill_url
  const follow = (end: 'ค่ะ' | 'นะคะ'): string => (link ? ` ติดตามสถานะได้ที่ลิงก์นี้${end} ${link}` : '')
  const l = o.lalamove
  const d = o.dhl
  const dayPhrase = dayWord(o.delivery_date, nowMs) ?? ''
  const round = o.slot ? ` รอบ ${o.slot}` : ''
  if (o.carrier === 'dhl') {
    const no = d?.tracking_no ? ` เลขพัสดุ ${d.tracking_no}` : ''
    const scan = scanFold(d, nowMs).latest
    const lastScan = (): string => {
      const s = d?.scans?.[0]
      if (!scan) return ''
      const at = s?.at ?? d?.event_at
      return ` สถานะล่าสุด: ${scan.text}${scan.place ? ` (${scan.place})` : ''}${at ? ` ${whenSaid(at, nowMs)}` : ''}`
    }
    switch (o.phase) {
      case 'scheduled':
      case 'preparing':
        return dayPhrase ? `ร้านกำลังเตรียมพัสดุค่ะ กำหนดส่งออกกับ DHL ${dayPhrase.trim()} นะคะ` : 'ร้านกำลังเตรียมพัสดุค่ะ'
      case 'label_created':
        return `ร้านออกเลขพัสดุ DHL แล้ว รอ DHL เข้ารับค่ะ${no}${follow('นะคะ')}`
      case 'delivered': {
        const at = o.delivered_at ?? d?.event_at
        return `พัสดุส่งถึงแล้ว${at ? whenSaid(at, nowMs) : ''} ค่ะ${d?.epod_name ? ` ผู้รับ ${d.epod_name}` : ''} ขอบคุณค่ะ`
      }
      case 'failed':
        return `ขออภัยค่ะ DHL แจ้งว่านำส่งไม่สำเร็จ${scan ? ` (${scan.text})` : ''} ทีมงานกำลังตรวจสอบกับ DHL ให้นะคะ${no}`
      case 'returned':
        return `ขออภัยค่ะ DHL แจ้งว่าพัสดุถูกตีกลับ ทีมงานกำลังตรวจสอบให้นะคะ${no}`
      case 'carrier_cancelled':
        return 'ขออภัยค่ะ ทีมงานกำลังตรวจสอบการจัดส่งของคำสั่งซื้อนี้ให้นะคะ'
      default: {
        if (d?.quiet_h && d.quiet_h >= DHL_QUIET_H) {
          return `ขออภัยค่ะ พัสดุยังไม่มีความเคลื่อนไหวใหม่จาก DHL ทีมงานกำลังตรวจสอบกับ DHL ให้นะคะ${lastScan()}${no}${follow('ค่ะ')}`
        }
        const lead =
          o.phase === 'picked_up'
            ? 'DHL รับพัสดุแล้วค่ะ'
            : o.phase === 'out_for_delivery'
              ? 'พัสดุอยู่ระหว่างนำส่งโดยพนักงาน DHL ค่ะ'
              : 'พัสดุอยู่ระหว่างขนส่งกับ DHL ค่ะ'
        return `${lead}${lastScan()}${no}${follow('นะคะ')}`
      }
    }
  }
  switch (o.phase) {
    case 'scheduled':
      return `คำสั่งซื้อนี้กำหนดส่ง${dayPhrase}${round} ค่ะ`
    case 'preparing':
      return `ร้านกำลังเตรียมอาหารค่ะ กำหนดส่ง${dayPhrase}${round} นะคะ`
    case 'finding_driver':
      return `ร้านเรียกรถแล้ว กำลังหาคนขับอยู่ค่ะ${follow('นะคะ')}`
    case 'to_pickup':
      return `คนขับกำลังไปรับอาหารที่ร้านค่ะ${follow('นะคะ')}`
    case 'on_the_way': {
      const arr = arrivalOf(l, nowMs)
      if (arr && o.timing && o.timing.late_min > 0) {
        const why = l?.stops_ahead && l.stops_ahead > 0 ? 'วันนี้คนขับมีจุดส่งก่อนหน้า' : 'อาหารอาจถึงช้ากว่ารอบส่ง'
        return `ขออภัยค่ะ ${why} คาดว่าจะถึงประมาณ ${arr} น. ค่ะ${follow('นะคะ')}`
      }
      if (arr) return `คนขับกำลังนำส่งค่ะ คาดว่าจะถึงประมาณ ${arr} น. นะคะ${follow('ค่ะ')}`
      const ahead = l?.stops_ahead && l.stops_ahead > 0 ? ` ก่อนถึงคุณลูกค้ามีจุดส่งอีก ${l.stops_ahead} จุดค่ะ` : ''
      return `คนขับรับอาหารแล้ว กำลังนำส่งค่ะ${ahead}${follow('นะคะ')}`
    }
    case 'delivered': {
      const at = o.delivered_at ?? l?.delivered_at
      return `${o.carrier === 'lalamove' ? 'คนขับส่งอาหารถึงแล้ว' : 'ส่งอาหารถึงแล้ว'}${at ? whenSaid(at, nowMs) : ''} ค่ะ ขอบคุณค่ะ`
    }
    case 'carrier_cancelled':
      return 'ขออภัยค่ะ งานจัดส่งรอบนี้ขัดข้อง ทีมงานกำลังตรวจสอบให้นะคะ'
    default:
      return link ? `คำสั่งซื้อนี้อยู่ระหว่างจัดส่งค่ะ${follow('นะคะ')}` : 'คำสั่งซื้อนี้อยู่ระหว่างจัดส่งค่ะ'
  }
}

/** Thai marks that must stay with the letter before them (a cut never lands between). */
const MARK = /[\u0E31\u0E34-\u0E3A\u0E47-\u0E4E]/

/** One-line preview of the message on the card ("…" in text, not a CSS clip); the full text goes in the tooltip. */
export function messagePreview(text: string, n = 48): string {
  if (text.length <= n) return text
  const space = text.lastIndexOf(' ', n)
  let i = space >= n * 0.6 ? space : n // end on a phrase break when there is one close enough
  while (i < text.length && MARK.test(text[i] ?? '')) i++
  return `${text.slice(0, i).trimEnd()}…`
}
